import { styled } from '@mui/material'
import { CaretLeftIcon, CaretRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react'
import { FloatingButton } from 'ComponentLibrary'
import { LegacyLoadingIcon } from 'ComponentLibrary/src/icons/legacyLoadingIcon'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

type FunnelFooterProps = {
    activeStep: number
    isLastStep: boolean
    handleNext: () => void
    isPending?: boolean
} & ({ onCancel: () => void; handleBack?: never } | { handleBack: () => void; onCancel?: never })

export function FunnelFooter({ activeStep, isLastStep, handleNext, isPending, ...props }: FunnelFooterProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage' })

    const getIcon = () => {
        if (isPending) {
            return <LegacyLoadingIcon spinTime={2} />
        }
        if (isLastStep) {
            return <CheckIcon />
        }
        return <CaretRightIcon />
    }

    const isFirstStep = activeStep === 0

    return (
        <ButtonContainer>
            {'onCancel' in props ? (
                <CancelButton onClick={props.onCancel} />
            ) : (
                <FloatingButton
                    onClick={props.handleBack}
                    label={t('footer.back')}
                    variant="secondary"
                    startIcon={<CaretLeftIcon />}
                    disabled={isPending || isFirstStep}
                />
            )}
            <FloatingButton
                onClick={handleNext}
                label={isLastStep ? t('footer.submit') : t('footer.next')}
                variant="primary"
                endIcon={getIcon()}
                disabled={isPending}
            />
        </ButtonContainer>
    )
}

type CancelButtonProps = Omit<ComponentProps<typeof FloatingButton>, 'label' | 'variant' | 'startIcon'>
const CancelButton = (props: CancelButtonProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage.footer' })

    return <FloatingButton label={t('cancel')} variant="secondary" startIcon={<XIcon />} {...props} />
}

export const ButtonContainer = styled('div')(({ theme }) => ({
    bottom: '48px',
    position: 'fixed',
    gap: '24px',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
        left: 'unset',
        right: '48px',
    },
    [theme.breakpoints.up('md')]: {
        right: 'unset',
        left: '62vw',
    },
}))
